import React from 'react';
import { logger } from '../infrastructure/logger';
import { safeReload, getCurrentUrl } from '../utils/safeNavigation';

interface Props {
    children: React.ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
}

export class GlobalErrorBoundary extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            hasError: false,
            error: null
        };
    }

    static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

override componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Log detallado del error
    logger.fatal('Error crítico no capturado', error, {
      componentStack: errorInfo.componentStack,
      url: getCurrentUrl(),
      userAgent: navigator.userAgent
    });
  }

    handleReset = (): void => {
        this.setState({ hasError: false, error: null });
        // Use safeReload to clear error state and reload app
        safeReload();
    };

    handleDeepRepair = (): void => {
        // Clear everything that could be causing permission/cache issues
        try {
            localStorage.clear();
            sessionStorage.clear();
            // Clear IndexedDB (Firebase default persistence)
            const dbs = ['firestore', 'firebase-auth-storage', 'firebase-heartbeat-database'];
            dbs.forEach(db => window.indexedDB.deleteDatabase(db));
            
            this.handleReset();
        } catch {
            this.handleReset();
        }
    };

    override render(): React.ReactNode {
        if (this.state.hasError) {
            const isPermissionError = this.state.error?.message?.toLowerCase().includes('permission') || 
                                     this.state.error?.message?.toLowerCase().includes('quota');

            return (
                <div className="flex flex-col items-center justify-center min-h-screen bg-background-light dark:bg-background-dark p-6 text-center">
                    <div className="w-20 h-20 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mb-6">
                        <span className="material-symbols-outlined text-4xl text-red-500">
                            {isPermissionError ? 'lock_reset' : 'error_outline'}
                        </span>
                    </div>
                    
                    <h1 className="text-2xl font-black text-text-main dark:text-white mb-2">
                        {isPermissionError ? 'Error de Conexión' : '¡Ups! Algo salió mal'}
                    </h1>
                    <p className="text-text-secondary dark:text-gray-400 mb-8 max-w-xs text-sm">
                        {isPermissionError 
                            ? 'Parece que hay un problema con tu sesión o la caché del navegador.' 
                            : 'Tuvimos un problema técnico inesperado.'}
                    </p>

                    <div className="flex flex-col gap-3 w-full max-w-xs">
                        <button 
                            onClick={this.handleReset}
                            className="bg-primary text-white font-bold py-3.5 rounded-xl shadow-lg hover:bg-orange-600 transition-all active:scale-95"
                        >
                            Intentar de nuevo
                        </button>
                        
                        <button 
                            onClick={this.handleDeepRepair}
                            className="text-text-secondary dark:text-gray-400 text-xs font-bold py-2 hover:underline"
                        >
                            Limpiar caché y Reparar App
                        </button>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}